import { Checkbox } from '@components/ui/checkbox';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { Avatar } from '@components/avatar';
import { cn } from '@utils/cn';
import { capitalizeFirstLetter } from '@utils/textHelpers';
import { JobTaskCalendarText } from './calendarText';
import {
  JobStage,
  JobSubtask as JobSubtaskType,
  JobTaskStatus,
  JobTask as JobTaskType,
} from '@root/globalTypes';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTime, parseRoute } from '@utils/i18n';
import { JobTaskPill } from './pill';
import { useState } from 'react';
import { useCompleteTask } from '@services/tasks/complete';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { RemoveConfirmationAlert } from '@components/dialogs/removeConfirmationAlert';
import { Button } from '@components/ui/button';
import { useRemoveExternalSchedule } from '@services/schedules/external/remove';
import { useRevertTask } from '@services/tasks/revert';
import { parseISO } from 'date-fns';
import { JobTaskRemove } from './remove';
import { useJobContext } from '@contexts/job';
import { JobTaskAction, getAction, setBackground, sortByProp } from '../helpers';
import { JobFlag } from '../flag';
import { JobSubtask } from '../subtask';
import { NoteBox } from '../noteBox';
import { Issue } from '@components/issue';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { appRoutes } from '@utils/consts';
import { Link, useParams } from 'react-router';
import { JobTaskFormCount } from './formCount';
import { JobTaskIntegrationTooltip } from './integrationTooltip';
import { DashboardItemPill } from '@components/dashboard/item/pill';
import { createSchedule } from '@contexts/scheduler/helpers';
import { useAccess } from '@hooks/access';

export const JobTask = ({
  data,
  date,
  stage,
}: {
  data: JobTaskType;
  date?: string;
  stage?: JobStage;
}) => {
  const action = getAction(data);
  const isNotRevertable = data.status === 'completed' && !data.forceCompleted;
  const [isRevertConfirm, setIsRevertConfirm] = useState(false);
  const { t } = useTranslation('app');
  const { job, project: projectData, archived, allowed, setState } = useJobContext();
  const { projectId } = useParams();
  const [id, jobId, scheduleId, name, address, lat, lon, division] = [
    data.id,
    job.data?.data.id || '',
    data.schedule?.id || '',
    job.data?.data.name || '',
    job.data?.data.address || '',
    job.data?.data.lat || '',
    job.data?.data.lon || '',
    job.data?.data.division || { id: '', name: '' },
  ];
  const project = {
    name: projectData?.jobName || job.data?.data.project?.name || '',
    address: projectData?.jobAddress || job.data?.data.project?.address || '',
  };

  const access = useAccess();
  const isCadTask = data?.name?.includes('CAD');

  const removeExternalSchedule = useRemoveExternalSchedule({
    id: scheduleId,
    jobId,
    taskId: id,
  });

  const removeInternalSchedule = useRemoveInternalSchedule({
    id: scheduleId,
    jobId,
    taskId: id,
  });

  const complete = useCompleteTask();
  const revert = useRevertTask();

  const handleCheckbox = (newValue: boolean) => {
    if (!id || !jobId) return;
    if (newValue) complete.mutate({ id, jobId });
    else setIsRevertConfirm(true);
  };

  const openRescheduler = () => {
    setState((prev) => ({
      ...prev,
      open: true,
      initialParams: {
        users: [data.schedule?.foreman?.id || ''],
        specialties: data.schedule?.foreman?.specialties?.map((el) => el.value),
      },
      initialDate: date,
      job: { id: jobId, projectId: projectId || '', name, address, lat, lon, division, project },
      task: { ...data, schedule: data.schedule || createSchedule(data, job.data?.data) },
      tasks: stage?.tasks || [],
    }));
  };

  const remove = {
    onConfirm:
      data.scheduleType === 'internal'
        ? () => removeInternalSchedule.mutate()
        : () => removeExternalSchedule.mutate(),
    locked:
      data.scheduleType === 'internal'
        ? removeInternalSchedule.isLoading
        : removeExternalSchedule.isLoading,
  };

  const variant: Record<JobTaskAction, string> = {
    schedule:
      'text-primary-400 border-primary-200 bg-white hover:text-white hover:bg-primary-400 hover:border-primary-400 disabled:bg-white disabled:text-primary-400 disabled:border-primary-200',
    none: '',
    form: 'text-neutral-700 border-transparent hover:underline',
    reschedule: `text-neutral-700 disabled:text-neutral-700 ${
      data.status === 'rejected'
        ? 'border-neutral-200 bg-white disabled:bg-white disabled:border-neutral-200 hover:bg-neutral-700 hover:border-neutral-700 hover:text-white'
        : 'border-transparent hover:underline disabled:no-underline'
    }`,
  };

  const scheduleDate = data.schedule?.startsAt ? parseISO(data.schedule?.startsAt) : undefined;

  const formButton = () => {
    if (!data.questionaryId) return null;

    return (
      <div className="flex items-center gap-1">
        <Link
          to={`/${parseRoute(appRoutes.jobQuestionnaire(projectId || '', jobId, data.questionaryId))}`}
          className={cn([
            'flex h-fit w-fit items-center gap-3 rounded-md py-1.5 text-sm font-semibold transition-colors disabled:opacity-50 ',
            variant.form,
          ])}
          type="button"
          onClick={openRescheduler}>
          {t('actions.form')}
          <SvgConsumer id="back" className="rotate-180 text-[10px]" />
        </Link>
        {data.removable && stage && <JobTaskRemove data={{ ...data, stageId: stage.id }} />}
      </div>
    );
  };

  const renderScheduleButton = () => {
    if (action === 'none') return null;
    if (action === 'form' && data?.questionaryId) return formButton();
    if (data.routePoint) {
      return (
        <AppTooltip side="top" context={t('schedules.routeLocked')}>
          <div className="flex cursor-not-allowed justify-end gap-1">
            <span
              className={cn([
                'flex h-fit w-fit items-center gap-2 rounded-md border px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50',
                variant[action],
              ])}>
              {capitalizeFirstLetter(action)}
            </span>
            <Button variant="ghost" size="sm" className="px-2" disabled>
              <SvgConsumer id="taskRemove" className="text-m" />
            </Button>
          </div>
        </AppTooltip>
      );
    }
    return (
      <div className="flex justify-end gap-1">
        {data?.questionaryId && formButton()}
        {data.scheduleType && data.scheduleType !== 'dependent' && (
          <>
            <button
              data-testid={`schedule-${data.name}`}
              disabled={
                archived ||
                access.cad ||
                (action === 'reschedule' && Boolean(data.concreteDirectOrderExternalId))
              }
              className={cn([
                'flex h-fit w-fit items-center gap-2 rounded-md border px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50',
                variant[action],
              ])}
              type="button"
              onClick={openRescheduler}>
              {(action === 'schedule' ||
                (action === 'reschedule' && data.status === 'rejected')) && (
                <SvgConsumer id="calendar" />
              )}
              {capitalizeFirstLetter(action)}
            </button>
            {action === 'schedule' && !data.removable && <div className="w-8" />}
          </>
        )}
        {action === 'schedule' && data.removable && stage && (
          <JobTaskRemove data={{ ...data, stageId: stage.id }} />
        )}
        {data?.scheduleType !== 'dependent' && action === 'reschedule' && (
          <RemoveConfirmationAlert
            title={t('schedules.removeTitle')}
            description={t('schedules.removeInfo')}
            onConfirm={remove.onConfirm}
            locked={remove.locked}>
            <Button
              data-testid={`remove-schedule-${data.name}`}
              title={t('actions.removeSchedule')}
              disabled={archived || access.cad}
              variant="ghost"
              size="sm"
              className="px-2">
              <SvgConsumer id="trash" className="text-m" />
              <span className="sr-only">{t('actions.removeSchedule')}</span>
            </Button>
          </RemoveConfirmationAlert>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={cn([
          'col-start-1 col-end-2 flex flex-col justify-center gap-1 border-t-2 border-neutral-200 px-6 py-3',
          setBackground(data.status as JobTaskStatus),
        ])}>
        <div className="flex items-center gap-4">
          <h3 className="flex h-[22px] items-center justify-center text-sm font-semibold text-neutral-800">
            {data.name}
          </h3>
          <JobTaskPill status={data.status} />
          <JobTaskFormCount
            count={data.totalNoOfAnsweredQuestions}
            total={data.totalNoOfQuestions}
          />
          {data.concreteDirectOrderExternalId && (
            <button type="button" onClick={openRescheduler}>
              <JobTaskIntegrationTooltip />
            </button>
          )}
          {data.status === 'completed' && data.forceCompleted && (
            <AppTooltip context={<p>{t('pages.job.info.doneTooltip')}</p>}>
              <span>
                <SvgConsumer className="text-xl text-success-800" id="markAsDone" />
              </span>
            </AppTooltip>
          )}
        </div>
        {data.schedule && scheduleDate && (
          <>
            <div className={cn(['flex gap-8', data.status === 'rejected' && 'opacity-50'])}>
              <div className="flex items-center gap-2">
                <SvgConsumer className="text-md text-neutral-400" id="calendar" />
                <JobTaskCalendarText>{formatDate(scheduleDate)}</JobTaskCalendarText>
                <JobTaskCalendarText>{formatTime(scheduleDate)}</JobTaskCalendarText>
              </div>
              {data.schedule.foreman && (
                <div className="flex items-center gap-2">
                  <Avatar
                    hideTooltip
                    name={[data.schedule.foreman.firstName, data.schedule.foreman.lastName].join(
                      ' ',
                    )}
                  />
                  <span className="whitespace-nowrap text-sm">
                    {[data.schedule.foreman.firstName, data.schedule.foreman.lastName].join(' ')}
                  </span>
                </div>
              )}
              {data.schedule.contact && (
                <span className="whitespace-nowrap text-sm">
                  {[data.schedule.contact.firstName, data.schedule.contact.lastName].join(' ')}
                </span>
              )}
            </div>
            {data.schedule.contact && !data.schedule.contact.phone && (
              <div className="w-fit">
                <DashboardItemPill size="sm">{t('schedules.noPhoneNumber')}</DashboardItemPill>
              </div>
            )}
          </>
        )}
      </div>
      <div
        className={cn([
          'col-start-2 col-end-3 flex items-center justify-end border-t-2 border-neutral-200 pr-6',
          setBackground(data.status as JobTaskStatus),
        ])}>
        {renderScheduleButton()}
      </div>
      <div
        className={cn([
          'col-start-3 col-end-4 flex justify-end border-t-2 border-neutral-200',
          setBackground(data.status as JobTaskStatus),
        ])}>
        <div className="w-fit border-x border-neutral-100 px-4.5 py-4">
          <Checkbox
            disabled={isNotRevertable || archived || !allowed}
            checked={data.status === 'completed'}
            onCheckedChange={handleCheckbox}
          />
        </div>
      </div>
      <div
        className={cn([
          'col-start-4 col-end-5 flex border-t-2 border-neutral-200',
          setBackground(data.status as JobTaskStatus),
        ])}>
        <NoteBox
          referableType="Task"
          data={data.note}
          jobId={jobId}
          typeId={id}
          disabled={archived || (!isCadTask && access.cad)}
        />
      </div>
      {Boolean(data.cancellationConfirmations?.length) &&
        data.cancellationConfirmations?.map((el) => (
          <JobFlag key={el.id} data={el} jobId={jobId} taskId={id} />
        ))}
      {Boolean(data.issues?.length) &&
        data.issues?.map((el) => <Issue key={el.id} data={el} jobId={jobId} taskId={id} />)}
      {(data.schedule || !data.scheduleType) &&
        sortByProp<JobSubtaskType>(data.subtasks || [], 'position', 'asc').map((subTask) => (
          <JobSubtask key={subTask.id} data={subTask} status={data.status} />
        ))}
      <RemoveConfirmationAlert
        open={isRevertConfirm}
        setOpen={setIsRevertConfirm}
        locked={false}
        title={t('confirmDialog.title')}
        description={t('pages.job.revertTask', {
          name: data.name,
        })}
        onConfirm={() => revert.mutate({ id, jobId })}
      />
    </>
  );
};
